import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  Plus, LogOut, Menu, X, Repeat, Home, Wifi, Phone, Tv2,
  ShieldCheck, Zap, Dumbbell, BookOpen, Heart, Car, Trash2,
  Pencil, Sparkles, PlayCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recurring presets — one click to pre-fill the dialog
const PRESETS = [
  { key: 'rent',         label: 'Alquiler',        icon: Home,         category: 'Vivienda',       description: 'Alquiler mensual',         day: 5,  color: '#8B5CF6' },
  { key: 'internet',     label: 'Internet',        icon: Wifi,         category: 'Servicios',      description: 'Internet residencial',     day: 10, color: '#3B82F6' },
  { key: 'phone',        label: 'Teléfono / Plan', icon: Phone,        category: 'Servicios',      description: 'Plan móvil',               day: 12, color: '#06B6D4' },
  { key: 'streaming',    label: 'Streaming',       icon: Tv2,          category: 'Entretenimiento',description: 'Netflix / Spotify / etc.', day: 15, color: '#EC4899' },
  { key: 'insurance',    label: 'Seguro',          icon: ShieldCheck,  category: 'Salud',          description: 'Seguro mensual',           day: 8,  color: '#10B981' },
  { key: 'utilities',    label: 'Luz / Agua / Gas',icon: Zap,          category: 'Servicios',      description: 'Servicios públicos',       day: 20, color: '#F59E0B' },
  { key: 'gym',          label: 'Gimnasio',        icon: Dumbbell,     category: 'Salud',          description: 'Mensualidad gym',          day: 1,  color: '#EF4444' },
  { key: 'subscription', label: 'Suscripción',     icon: PlayCircle,   category: 'Entretenimiento',description: 'Otra suscripción',         day: 1,  color: '#A855F7' },
  { key: 'education',    label: 'Educación',       icon: BookOpen,     category: 'Educación',      description: 'Curso / colegiatura',      day: 5,  color: '#6366F1' },
  { key: 'donation',     label: 'Donación',        icon: Heart,        category: 'Otros',          description: 'Donación mensual',         day: 1,  color: '#F472B6' },
  { key: 'car',          label: 'Coche',           icon: Car,          category: 'Transporte',     description: 'Cuota / seguro vehículo',  day: 15, color: '#14B8A6' },
];

const RecurringPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [recurring, setRecurring] = useState([]);
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    label: '',
    description: '',
    category: 'Otros',
    amount: '',
    day_of_month: 5,
    account_id: '',
    notes: '',
    color: '#7C3AED'
  });
  const [presetIcon, setPresetIcon] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [r, a] = await Promise.all([
        fetch(`${API_URL}/api/recurring-expenses`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/accounts`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setRecurring(r);
      setAccounts(a);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openPreset = (preset) => {
    setForm({
      label: preset.label,
      description: preset.description,
      category: preset.category,
      amount: '',
      day_of_month: preset.day,
      account_id: '',
      notes: '',
      color: preset.color
    });
    setPresetIcon(preset.icon);
    setDialogOpen(true);
  };

  const openManual = () => {
    setForm({
      label: '',
      description: '',
      category: 'Otros',
      amount: '',
      day_of_month: 5,
      account_id: '',
      notes: '',
      color: '#7C3AED'
    });
    setPresetIcon(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/recurring-expenses`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'expense',
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description || form.label,
        day_of_month: parseInt(form.day_of_month, 10),
        account_id: form.account_id || null,
        notes: form.notes || null
      })
    });
    setDialogOpen(false);
    fetchAll();
  };

  const toggle = async (r) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/recurring-expenses/${r.id}/toggle`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` }
    });
    fetchAll();
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la recurrente "${r.description}"?`)) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/recurring-expenses/${r.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    fetchAll();
  };

  const generate = async () => {
    setGenerating(true);
    setGenMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/recurring-expenses/generate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGenMsg(data.generated > 0
          ? `${data.generated} transacción(es) generada(s) para este mes`
          : 'Todo al día — no había recurrentes pendientes');
        setTimeout(() => setGenMsg(''), 4000);
        fetchAll();
      }
    } finally {
      setGenerating(false);
    }
  };

  const accountMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const totalMonthly = recurring
    .filter(r => r.is_active && r.type === 'expense')
    .reduce((s, r) => s + r.amount, 0);

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="recurring" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="recurring" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Recurrentes</h2>
              <p className="text-zinc-400 text-sm">Alquiler, internet, suscripciones y otras facturas mensuales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
              onClick={generate}
              disabled={generating || recurring.length === 0}
              data-testid="generate-recurring-btn"
            >
              <Repeat className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Generar este mes
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={openManual}
              data-testid="new-recurring-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Manual
            </Button>
          </div>
        </div>

        {genMsg && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="gen-msg">
            {genMsg}
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-400 mb-1">Recurrentes activos</p>
              <p className="text-2xl font-bold text-white">{recurring.filter(r => r.is_active).length}</p>
              <p className="text-xs text-zinc-500 mt-1">de {recurring.length} total</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-400 mb-1">Total mensual estimado</p>
              <p className="text-2xl font-bold text-red-400">{fmt(totalMonthly)}</p>
              <p className="text-xs text-zinc-500 mt-1">solo gastos activos</p>
            </CardContent>
          </Card>
        </div>

        {/* === PRESETS === */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-400" />
            Plantillas rápidas
          </h3>
          <p className="text-sm text-zinc-400 mb-4">
            Un clic para precargar el formulario con los datos típicos. Solo te queda poner el monto.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="presets-grid">
            {PRESETS.map(p => {
              const Icon = p.icon;
              return (
                <button
                  key={p.key}
                  onClick={() => openPreset(p)}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-zinc-700 transition-all"
                  data-testid={`preset-${p.key}`}
                >
                  <div className="h-12 w-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                       style={{ backgroundColor: `${p.color}22` }}>
                    <Icon className="h-6 w-6" style={{ color: p.color }} />
                  </div>
                  <span className="text-sm font-medium text-white text-center">{p.label}</span>
                  <span className="text-[10px] text-zinc-500 text-center">{p.category} · día {p.day}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* === LIST === */}
        <h3 className="text-lg font-bold text-white mb-3">Tus recurrentes</h3>
        {loading ? (
          <p className="text-zinc-500">Cargando…</p>
        ) : recurring.length === 0 ? (
          <Card className="bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30">
            <CardContent className="p-8 text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-violet-500/20 items-center justify-center mb-4">
                <Repeat className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Sin recurrentes</h3>
              <p className="text-zinc-400 mb-6">
                Elige una plantilla rápida arriba, o crea una manualmente.
              </p>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={openManual}>
                <Plus className="h-4 w-4 mr-2" /> Crear manual
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="p-0">
              {recurring.map((r) => (
                <div
                  key={r.id}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-800 last:border-0 ${!r.is_active ? 'opacity-50' : ''}`}
                  data-testid={`recurring-row-${r.id}`}
                >
                  <div className="h-10 w-10 shrink-0 rounded-full bg-violet-500/15 flex items-center justify-center">
                    <Repeat className="h-4 w-4 text-violet-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-white truncate">{r.description}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {r.category} · día {r.day_of_month}
                      {r.account_id && accountMap[r.account_id]
                        ? <> · <span style={{ color: accountMap[r.account_id].color }}>{accountMap[r.account_id].name}</span></>
                        : ''}
                      {!r.is_active && ' · Pausada'}
                    </p>
                  </div>
                  <div className="text-right shrink-0 mr-2">
                    <p className="font-semibold text-red-400">{fmt(r.amount)}</p>
                    {r.last_generated && (
                      <p className="text-[10px] text-zinc-500">últ. {r.last_generated}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost" size="sm"
                      className="text-zinc-400 hover:text-violet-300 h-8 px-2"
                      onClick={() => toggle(r)}
                      data-testid={`toggle-recurring-${r.id}`}
                    >
                      {r.is_active ? 'Pausar' : 'Activar'}
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-red-400"
                      onClick={() => remove(r)}
                      aria-label={`Eliminar ${r.description}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Create dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {presetIcon && (() => {
                  const Icon = presetIcon;
                  return (
                    <span className="h-9 w-9 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${form.color}22` }}>
                      <Icon className="h-5 w-5" style={{ color: form.color }} />
                    </span>
                  );
                })()}
                {form.label || 'Nuevo recurrente'}
              </DialogTitle>
              <DialogDescription>
                Se generará automáticamente cada mes al pulsar "Generar este mes".
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="rec-desc">Descripción</Label>
                <Input id="rec-desc" required value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Ej: Alquiler depto" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="rec-amount">Monto mensual</Label>
                  <Input id="rec-amount" required inputMode="decimal" type="number" step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="1500.00"
                    autoFocus
                    data-testid="rec-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="rec-day">Día de pago</Label>
                  <Input id="rec-day" required inputMode="numeric" type="number" min="1" max="28"
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div>
                <Label htmlFor="rec-cat">Categoría</Label>
                <Input id="rec-cat" required value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label htmlFor="rec-acc">Cuenta (opcional)</Label>
                <Select
                  value={form.account_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, account_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger id="rec-acc" className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Sin cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cuenta</SelectItem>
                    {accounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} {a.type === 'credit_card' ? '· tarjeta' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rec-notes">Notas (opcional)</Label>
                <Input id="rec-notes" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Detalles adicionales" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-recurring-btn">
                  Guardar recurrente
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default RecurringPage;
